import {
  ArticleCategory,
  UserInputKey
} from "@input-output-hk/essential-cardano-models";
import {
  isValidSlug,
  normalizeSlug
} from "@input-output-hk/essential-cardano-utils";
import { StructuredCommentThread } from "../../../data/github/types/comment";
import { isValidHttpUrl, parseUrl } from "../../../utils/url-utils";

type FormSchemaType = ArticleCategory["type"];

type FieldName = StructuredCommentThread["about"];

export type ContributeFieldType =
  | "text"
  | "slug"
  | "markdown-large"
  | "markdown"
  | "tags"
  | "image";

export const ImageUploadField = Symbol.for("__image_upload__");

type ImageUploadFieldType = typeof ImageUploadField;

export const isImageUploadField = (f: any): f is ImageUploadFieldType => {
  return f === ImageUploadField;
};

type FormSchemaField = {
  name: UserInputKey;
  label: string;
  type: ContributeFieldType;
  placeholder?: string;
  validation?: (value: unknown) => { error: string } | undefined | null;
};

export type ContributeFormSchemaTemplate = {
  type: FormSchemaType;
  fields: (FormSchemaField | ImageUploadFieldType)[];
};

type ContributeFormSchemaTemplates = Record<
  FormSchemaType,
  ContributeFormSchemaTemplate
>;

const titleField: FormSchemaField = {
  name: "title",
  label: "Title",
  type: "text",
  placeholder: "e.g. How to join a stake pool",
  validation: (title: string | undefined) =>
    !title?.trim() ? { error: "Please provide a title" } : undefined
};

/**
 * Sets the last part of the published URL i.e.
 * essentialcardano.io/<category>/<slug>
 *
 * Optional - when left blank we derive one from the title
 */
const slugField: FormSchemaField = {
  name: "slug",
  label: "URL slug (optional)",
  type: "slug",
  placeholder: "e.g. how-to-join-a-stake-pool",
  validation: (slug: string | undefined) => {
    const value = slug?.trim();
    if (!value) {
      // We fall back to a slug based on the title
      return undefined;
    }
    const suggestion = normalizeSlug(value);
    if (!suggestion) {
      return {
        error:
          "Please use lowercase letters, numbers and hyphens, e.g. how-to-join-a-stake-pool"
      };
    }
    if (!isValidSlug(value)) {
      return {
        error: `Please use lowercase letters, numbers and hyphens only, e.g. ${suggestion}`
      };
    }
    return undefined;
  }
};

const tagsField: FormSchemaField = {
  name: "_tags",
  label: "Tags",
  type: "tags",
  placeholder: "Tag your content",
  validation: (tags: string[] | undefined) =>
    !tags || tags.length < 1
      ? { error: "Please add at least one tag" }
      : undefined
};

const baseBodyField: FormSchemaField = {
  name: "body",
  label: "Description",
  type: "markdown",
  placeholder: "Briefly explain what you are sharing",
  validation: (body: string | undefined) =>
    !body?.trim()
      ? { error: "Your contribution should not be blank" }
      : undefined
};

const baseUrlField: FormSchemaField = {
  name: "url",
  type: "text",
  label: "URL",
  placeholder: "e.g. https://example.com",
  validation: (url: string | undefined) =>
    url && !isValidHttpUrl(parseUrl(url))
      ? {
          error: "Please enter a link that starts with http:// or https://"
        }
      : undefined
};

const baseRequiredUrlField: FormSchemaField = {
  ...baseUrlField,
  validation: (url: string | undefined) => {
    if (!url?.trim()) {
      return {
        error: "Please provide a URL"
      };
    } else {
      return baseUrlField.validation!(url);
    }
  }
};

export const ArticleTemplate: ContributeFormSchemaTemplate = {
  type: "article",
  fields: [
    titleField,
    slugField,
    ImageUploadField,
    {
      ...baseBodyField,
      label: "Content",
      type: "markdown-large",
      placeholder: "Write your article here"
    },
    tagsField
  ]
};

const FaqTemplate: ContributeFormSchemaTemplate = {
  type: "faq",
  fields: [
    titleField,
    slugField,
    ImageUploadField,
    {
      ...baseBodyField,
      label: "Description",
      placeholder: "Write your FAQ here"
    },
    tagsField
  ]
};

const InfographicTemplate: ContributeFormSchemaTemplate = {
  type: "infographic",
  fields: [
    titleField,
    slugField,
    ImageUploadField,
    {
      ...baseBodyField,
      label: "Description",
      placeholder: "Briefly explain your infographic"
    },
    tagsField
  ]
};

const VideoTemplate: ContributeFormSchemaTemplate = {
  type: "video",
  fields: [
    titleField,
    slugField,
    {
      ...baseRequiredUrlField,
      label: "Video URL",
      placeholder: "e.g. https://www.youtube.com/watch?v=R0s3lPG8XDw"
    },
    {
      ...baseBodyField,
      label: "Description",
      placeholder: "Briefly explain your video"
    },
    tagsField
  ]
};

const PodcastTemplate: ContributeFormSchemaTemplate = {
  type: "podcast",
  fields: [
    titleField,
    slugField,
    {
      ...baseRequiredUrlField,
      label: "Podcast URL",
      placeholder: "Your podcast URL"
    },
    {
      ...baseBodyField,
      label: "Description",
      placeholder: "Briefly explain your podcast"
    },
    tagsField
  ]
};

const DeveloperResourceTemplate: ContributeFormSchemaTemplate = {
  type: "developer",
  fields: [
    titleField,
    slugField,
    {
      ...baseUrlField,
      label: "URL",
      placeholder: "e.g. https://example.com/interesting-link"
    },
    ImageUploadField,
    {
      ...baseBodyField,
      label: "Description",
      placeholder: "Briefly explain what you are sharing"
    },
    tagsField
  ]
};

const OtherResourceTemplate: ContributeFormSchemaTemplate = {
  type: "developer",
  fields: [
    titleField,
    slugField,
    {
      ...baseUrlField,
      label: "URL",
      placeholder: "e.g. https://example.com/interesting-link"
    },
    ImageUploadField,
    {
      ...baseBodyField,
      label: "Description",
      placeholder: "Briefly explain what you are sharing"
    },
    tagsField
  ]
};

export const GlossaryTemplate: ContributeFormSchemaTemplate = {
  type: "glossary",
  fields: [
    {
      name: "title",
      label: "Title",
      type: "text",
      placeholder: "e.g. Glossary of Cardano terms"
    },
    slugField,
    {
      ...baseBodyField,
      label: "Content",
      type: "markdown",
      placeholder: "Write your glossary here"
    },
    tagsField
  ]
};

function templateToFieldNames(template: ContributeFormSchemaTemplate) {
  const orderedNames = template.fields.reduce((names, field) => {
    if (field === ImageUploadField) {
      names.push("image", "image_text");
    } else {
      names.push(field.name);
    }
    return names;
  }, [] as FieldName[]);
  return orderedNames;
}

export const Templates: ContributeFormSchemaTemplates = {
  article: ArticleTemplate,
  faq: FaqTemplate,
  video: VideoTemplate,
  podcast: PodcastTemplate,
  developer: DeveloperResourceTemplate,
  other: OtherResourceTemplate,
  glossary: GlossaryTemplate,
  "development-update": ArticleTemplate,
  infographic: InfographicTemplate
} as const;

/**
 * A map of the ordered field names based on the form template.
 *
 * We use this to sort the comments from GH into the same order
 * that the UI will be shown.
 */
export const TemplateFieldNameOrder: Record<FormSchemaType, FieldName[]> = {
  article: templateToFieldNames(ArticleTemplate),
  faq: templateToFieldNames(FaqTemplate),
  video: templateToFieldNames(VideoTemplate),
  podcast: templateToFieldNames(PodcastTemplate),
  developer: templateToFieldNames(DeveloperResourceTemplate),
  other: templateToFieldNames(Templates["other"]),
  glossary: templateToFieldNames(GlossaryTemplate),
  "development-update": templateToFieldNames(ArticleTemplate),
  infographic: templateToFieldNames(InfographicTemplate)
};
